import { FunctionComponent, useContext, useEffect, useState } from 'react';
import './index.scss';
import { Grid, Link, TextField } from '@mui/material';
import DataTable from '../grid';
import Button from '../button';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import ClaimAdjust from '../adjust';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import {
  IInspectionContext,
  InspectionContext,
  uploadMediaFile,
} from '@context/inspections';
import { ActionType } from '@src/types/action';
import { formatNumberPoint, renderComponentIsEnable } from '@shared/utils';
import FileInput from '../inputFile';
import { PhotoSection } from '@src/types/images';
import {
  BUCKET_NAME_CLAIMS_TRACKER,
  BUCKET_REGION_CLAIMS_TRACKER,
} from '@shared/api/config';
import DialogComponent from '@components/common/dialog';
import { ClaimantType, IClaimForm } from '@src/types/claimsTracker';
import { CountryCode, MetadataTag, ResourceAction } from '@src/types/common';
import {
  getOrCreateDamageAnalysisCPE,
  postEstimatesCPE,
} from '@shared/hooks/useStoreToCPE';
import { InputsEstimated, porcTaxesCountryConfig } from './inputs/inputs';
import { CPEAPIClient } from '@shared/api';
import { IClaimState } from '@src/types/claim';

export interface ivuAmounts {
  ivuPartsAmount: number;
  ivuLaborAmount: number;
}

export interface EstimatedAmounts {
  partsCost: string;
  porcDesc: string;
  discountAmount: string;
  subtotalParts: string;
  partsCostWithoutDiscount: string;
  partsCostTotal: string;
  expenseAmount: string;
  materialsCost: string;
  laborCost: string;
  ivuLaborAmount: string;
  ivuPartsAmount: string;
  estimatedTotalAmount: string;
  porcIVA: string;
  ivaAmount: string;
  previousDamageCost: string;
  jsonCustomEstimateData?: string;
  customEstimationId?: string;
}

export interface BalanceAmounts {
  claimUId: string;
  CustomEstimationId: string;
  jsonBalanceData?: string;
  balanceId?: string;
}

export interface DepreciationEstimatesData {
  claimId: string;
  damageAnalysisId: string;
  requiresDepreciationCalculation: boolean;
  carYear: number;
  carAge: number;
  depreciationRatePerYear: number;
  depreciationByYear: number;
  distanceDriven: number;
  distanceDrivenSegmentLength: number;
  depreciationRatePerDistanceDriven: number;
  depreciationByDistance: number;
  averageDepreciation: number;
  depreciationPercentageToApply: number;
  exceededDepreciationMaximum: boolean;
}

interface DepreciationEstimatesProp {
  data: DepreciationEstimatesData;
}

export const calculateDiscount = (
  porcDesc: number,
  partsCost: number,
): number => {
  if (porcDesc > 0 && porcDesc <= 100) {
    return (partsCost * porcDesc) / 100;
  }

  return 0;
};

export const calculateIVAAmount = (
  porcIVA: number,
  subtotal: number,
): number => {
  if (porcIVA > 0 && porcIVA <= 100) {
    return (subtotal * porcIVA) / 100;
  }

  return 0;
};

export const calculateIVUAmounts = (
  subtotalParts: number,
  subtotalLabor: number,
  countryCode: CountryCode,
): ivuAmounts | undefined => {
  const porcTaxes = porcTaxesCountryConfig[countryCode];

  if (!porcTaxes) {
    return undefined;
  }

  const ivuLaborAmount = calculateIVAAmount(porcTaxes.ivuLabor, subtotalLabor);
  const ivuPartsAmount = calculateIVAAmount(porcTaxes.ivuParts, subtotalParts);

  return { ivuLaborAmount, ivuPartsAmount };
};

export const calculateSubtotal = (
  partsCost: number,
  discount: number,
): number => {
  return partsCost - discount;
};

export const calculateCostTotalParts = (
  subtotal: number,
  partCostWithoutDiscount: number,
): number => {
  return subtotal + partCostWithoutDiscount;
};

export const calculateTotalEstimated = (
  costTotalParts: number,
  expenseAmount: number,
  materialsCost: number,
  laborCost: number,
  ivuLabor: number,
  ivuParts: number,
  ivaAmount: number,
  previousDamageCost: number,
): number => {
  return (
    costTotalParts +
    expenseAmount +
    materialsCost +
    laborCost +
    ivuLabor +
    ivuParts +
    ivaAmount -
    previousDamageCost
  );
};

interface PropsEstimated {
  claimUId?: string;
  claimNumber: string;
  poNumber: string;
  claimantType: string;
  disabled: boolean;
  accountReference?: number;
  policyNumber?: string;
  claimForm?: IClaimForm;
  CPEClaim: IClaimState;
  cpeIds?: {
    cpeClaimId: string;
    cpeDamageAnalysisId: string;
  };
}

const ClaimEstimated: FunctionComponent<PropsEstimated> = ({
  claimUId,
  claimNumber,
  poNumber,
  claimantType,
  disabled,
  accountReference,
  policyNumber,
  claimForm,
  CPEClaim,
  cpeIds,
}): JSX.Element => {
  const [fileUpload, setFileUpload] = useState<File>();
  const [fileName, setFileName] = useState('');
  useEffect(() => {
    getAllEstimatedAmount().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { dispatch, state } = useContext<IInspectionContext>(InspectionContext);
  const [dataHistoric, setDataHistoric] = useState<any>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [depreciationEstimates, setDepreciationEstimates] = useState<
    DepreciationEstimatesData
  >({
    claimId: '',
    damageAnalysisId: '',
    requiresDepreciationCalculation: false,
    carYear: 0,
    carAge: 0,
    depreciationRatePerYear: 0,
    depreciationByYear: 0,
    distanceDriven: 0,
    distanceDrivenSegmentLength: 0,
    depreciationRatePerDistanceDriven: 0,
    depreciationByDistance: 0,
    averageDepreciation: 0,
    depreciationPercentageToApply: 0,
    exceededDepreciationMaximum: false,
  });
  const [
    requiresDepreciationByPolicyType,
    setRequiresDepreciationByPolicyType,
  ] = useState(false);
  const [amountsState, setAmountsState] = useState<EstimatedAmounts>({
    partsCost: '',
    porcDesc: '0',
    discountAmount: '',
    subtotalParts: '',
    partsCostWithoutDiscount: '',
    partsCostTotal: '',
    expenseAmount: '',
    materialsCost: '',
    laborCost: '',
    ivuLaborAmount: '',
    ivuPartsAmount: '',
    estimatedTotalAmount: '',
    porcIVA: '0',
    ivaAmount: '0',
    previousDamageCost: '0',
  });

  const [selectionFields, setSelectionFields] = useState<GridRowId[]>([]);
  const [openDialogConfirm, setOpenDialogConfirm] = useState(false);

  const columns: GridColDef[] = [
    { field: 'estimatedTotalAmount', headerName: 'Estimado', width: 100 },
    { field: 'createdDate', headerName: 'Fecha', width: 180 },
    { field: 'userName', headerName: 'Usuario', width: 100 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      renderCell: (params) => (
        <Link
          className="claim-estimated__table--link"
          href="#"
          onClick={() => loadEstimated(params.id)}
        >
          Ver detalle
        </Link>
      ),
    },
    {
      field: 'fileUrl',
      headerName: 'Documentos',
      width: 150,
      renderCell: (params) => (
        <Link
          className="claim-estimated__table--link"
          target="_blank"
          href={params.value}
          hidden={!params.value}
        >
          Ver documento
        </Link>
      ),
    },
  ];

  const loadEstimated = (id: any): void => {
    const register = dataHistoric.find((x) => x.id === id);
    calculateEstimatedCountry(register);
  };

  const handleCancel = (): void => {
    setSelectionFields([]);
  };

  const openConfirmDialog = (): void => {
    setOpenDialogConfirm(true);
  };

  const handleConfirm = async (): Promise<void> => {
    setOpenDialogConfirm(false);
    await handleDelete();
  };

  const handleDelete = async (): Promise<void> => {
    try {
      dispatch({ type: ActionType.SET_LOADING, payload: true });

      for (const id of selectionFields) {
        await new ClaimsTrackerClient().DeleteEstimatedAmount(id as number);
      }

      setSelectionFields([]);
      await getAllEstimatedAmount();

      dispatch({ type: ActionType.SET_LOADING, payload: false });
    } catch (error) {
      console.error(error);
      dispatch({ type: ActionType.SET_LOADING, payload: false });
    }
  };

  useEffect(() => {
    if (state.expenseAmount) {
      const data = amountsState;

      switch (localStorage.getItem('countryCode') as CountryCode) {
        case CountryCode.COL:
          data.expenseAmount = state.expenseAmount || data.expenseAmount || '0';
          break;
        default:
          data.expenseAmount = data.expenseAmount || state.expenseAmount || '0';
          break;
      }

      setAmountsState({ ...data });
      calculateEstimatedCountry(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.expenseAmount]);

  useEffect(() => {
    if (state.partsAmount) {
      const data = amountsState;

      switch (localStorage.getItem('countryCode') as CountryCode) {
        case CountryCode.COL:
          data.partsCost = state.partsAmount || data.partsCost || '0';
          break;
        default:
          data.partsCost = data.partsCost || state.partsAmount || '0';
          break;
      }

      setAmountsState({ ...data });
      calculateEstimatedCountry(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.partsAmount]);

  useEffect(() => {
    const getDepreciationEstimatesData = async () => {
      if (!cpeIds || !cpeIds.cpeClaimId) {
        // If no claimId from CPE, return empty object
        return {};
      }
      // CPE claim must have the same PO number as SIC claim
      // CPE claim should also have an established relationship with the vehiclesInvolved and vehicle table
      const endpoint = `/v1/claims/${cpeIds?.cpeClaimId}/damageAnalysis/${cpeIds?.cpeClaimId}/depreciation`;
      const depreciationEstimatesResponse = await CPEAPIClient.get(endpoint);
      const depreciationEstimatesResponseData =
        depreciationEstimatesResponse?.data;

      return depreciationEstimatesResponseData;
    };
    const fetchDepreciationEstimatesData = async () => {
      const depreciationEstimatesData = await getDepreciationEstimatesData();
      setDepreciationEstimates(depreciationEstimatesData);
    };
    fetchDepreciationEstimatesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpeIds?.cpeClaimId]);

  useEffect(() => {
    const requiresDepreciationByPolicyType = (() => {
      if (!CPEClaim) {
        return false;
      } else if (
        CPEClaim.policy?.policyType.toUpperCase() === 'PAP' ||
        CPEClaim?.policy?.policyType.toUpperCase() === 'DAP'
      ) {
        return true;
      } else if (
        CPEClaim.policy?.policyType.toUpperCase() === 'SRO' ||
        CPEClaim?.policy?.policyType.toUpperCase() === 'LAP'
      ) {
        return false;
      } else {
        return false;
      }
    })();
    setRequiresDepreciationByPolicyType(requiresDepreciationByPolicyType);
  }, [CPEClaim]);

  const getAllEstimatedAmount = async (): Promise<void> => {
    try {
      dispatch({
        type: ActionType.SET_ESTIMATED_AMOUNT,
        payload: undefined,
      });
      const result = await new ClaimsTrackerClient().getAllEstimatedAmount(
        claimUId,
      );

      for (let i = 0; i < result.length; i++) {
        result[i].estimatedTotalAmount = formatNumberPoint(
          result[i].estimatedTotalAmount,
        );
        result[i].partsCost = formatNumberPoint(result[i].partsCost);
        result[i].materialsCost = formatNumberPoint(result[i].materialsCost);
        result[i].laborCost = formatNumberPoint(result[i].laborCost);
        result[i].expenseAmount = formatNumberPoint(result[i].expenseAmount);
        result[i].ivuPartsAmount = formatNumberPoint(result[i].ivuPartsAmount);
        result[i].partsCostTotal = formatNumberPoint(result[i].partsCostTotal);
        result[i].ivuLaborAmount = formatNumberPoint(result[i].ivuLaborAmount);
        result[i].previousDamageCost = formatNumberPoint(
          result[i].previousDamageCost,
        );
      }

      if (result?.length > 0) {
        const data = result.slice(-1)[0];
        dispatch({
          type: ActionType.SET_ESTIMATED_AMOUNT,
          payload: '1',
        });
        setAmountsState({ ...data });
        calculateEstimatedCountry({ ...data });
      }

      setDataHistoric(result);
    } catch (error) {
      console.warn(error);
    }
  };

  const validateAmounts = (): boolean => {
    if (disabled) {
      return true;
    }

    const countryCode = localStorage.getItem('countryCode') as CountryCode;
    const columns = InputsEstimated[countryCode];

    for (const column of columns) {
      for (const row of column.rows) {
        for (const input of row.inputsFields) {
          if (input.required && !amountsState[input.value]) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const onClickSave = async (): Promise<void> => {
    try {
      dispatch({ type: ActionType.SET_LOADING, payload: true });

      let fileUrl;

      if (fileUpload) {
        if (fileUpload.size <= 3000000) {
          const upload = await uploadMediaFile(
            fileUpload,
            `${poNumber}/${claimNumber}`,
            claimantType?.toUpperCase() === ClaimantType.CLAIM_INJURED
              ? PhotoSection.INJURED_OTHERS_DOCS
              : PhotoSection.INSURED_OTHERS_DOCS,
            BUCKET_NAME_CLAIMS_TRACKER,
            BUCKET_REGION_CLAIMS_TRACKER,
            `mitchell_${Date.now().toString()}`,
            MetadataTag.CMT_ESTIMATED_MITCHELL_DOCUMENT,
          );

          fileUrl = upload.urlUpload?.split('?')[0];
          setFileName('');
          setFileUpload(undefined);
        } else {
          setOpenDialog(true);
          dispatch({ type: ActionType.SET_LOADING, payload: false });

          return;
        }
      }

      const result = await new ClaimsTrackerClient().SaveEstimatedAmount(
        amountsState,
        claimUId,
        fileUrl,
      );

      if (cpeIds?.cpeClaimId) {
        const damageAnalysisId = await getOrCreateDamageAnalysisCPE(
          cpeIds.cpeClaimId,
          -1,
        );

        if (!damageAnalysisId) {
          console.warn('Error to send estimated to CPE');
        } else {
          await postEstimatesCPE(
            cpeIds.cpeClaimId,
            damageAnalysisId,
            amountsState,
          );
        }
      }

      if (result?.data?.success) {
        await getAllEstimatedAmount();
        dispatch({ type: ActionType.SET_LOADING, payload: false });
      }
    } catch (e) {
      console.warn(e);
      dispatch({ type: ActionType.SET_LOADING, payload: false });
    }
  };

  const onCalcEstimated = (event: any): void => {
    if (amountsState) {
      const data = amountsState;
      data[event?.target?.name] = event?.target?.value.replace(/[^0-9.]/g, '');
      setAmountsState({ ...data });

      calculateEstimatedCountry(data);
    }
  };

  const calculateEstimatedCountry = (data: EstimatedAmounts): void => {
    switch (localStorage.getItem('countryCode') as CountryCode) {
      case CountryCode.PRI:
      case CountryCode.PAN:
        calculateEstimated(data);
        break;
      case CountryCode.COL:
      case CountryCode.CRC:
        calculateEstimatedCOL(data);
        break;
    }
  };

  const calculateEstimatedCOL = (data: EstimatedAmounts): void => {
    const partsCost = Number(data.partsCost ?? 0);
    const laborCost = Number(data.laborCost ?? 0);
    const others = Number(data.expenseAmount ?? 0);
    const ivaAmount = calculateIVAAmount(
      Number(data.porcIVA ?? 0),
      laborCost + partsCost + others,
    );

    const totalEstimated = calculateTotalEstimated(
      partsCost,
      others,
      0,
      laborCost,
      0,
      0,
      ivaAmount,
      0,
    );

    data.partsCostWithoutDiscount = '0';
    data.porcDesc = '0';
    data.materialsCost = '0';
    data.ivuLaborAmount = ivaAmount.toFixed(2);
    data.ivaAmount = ivaAmount.toFixed(2);
    data.ivuPartsAmount = '0';

    data.partsCostTotal = partsCost.toFixed(2);
    data.estimatedTotalAmount = totalEstimated.toFixed(2);
    data.previousDamageCost = '0';

    setAmountsState({ ...data });
  };

  const calculateEstimated = (data: EstimatedAmounts): void => {
    const porcDesc = Number(data.porcDesc ?? 0);
    const partsCost = Number(data.partsCost ?? 0);
    const partCostWithoutDiscount = Number(data.partsCostWithoutDiscount ?? 0);
    const expenseAmount = Number(data.expenseAmount ?? 0);
    const materialsCost = Number(data.materialsCost ?? 0);
    const laborCost = Number(data.laborCost ?? 0);
    let ivuLabor = Number(data.ivuLaborAmount ?? 0);
    let ivuParts = Number(data.ivuPartsAmount ?? 0);
    const previousDamageCost = Number(data.previousDamageCost ?? 0);

    const discount = calculateDiscount(porcDesc, partsCost);
    const subtotal = calculateSubtotal(partsCost, discount);
    const costTotalParts = calculateCostTotalParts(
      subtotal,
      partCostWithoutDiscount,
    );

    const ivuTaxes = calculateIVUAmounts(
      costTotalParts,
      laborCost,
      localStorage.getItem('countryCode') as CountryCode,
    );

    if (ivuTaxes) {
      data.ivuLaborAmount = ivuTaxes.ivuLaborAmount.toFixed(2);
      data.ivuPartsAmount = ivuTaxes.ivuPartsAmount.toFixed(2);
      ivuLabor = ivuTaxes.ivuLaborAmount;
      ivuParts = ivuTaxes.ivuPartsAmount;
    }

    data.discountAmount = discount.toFixed(2);
    data.subtotalParts = subtotal.toFixed(2);
    data.partsCostTotal = costTotalParts.toFixed(2);

    const totalEstimated = calculateTotalEstimated(
      costTotalParts,
      expenseAmount,
      materialsCost,
      laborCost,
      ivuLabor,
      ivuParts,
      0,
      previousDamageCost,
    );

    data.estimatedTotalAmount = totalEstimated.toFixed(2);

    setAmountsState({ ...data });
  };

  const getSubtotalCalcCountry = () => {
    if (localStorage.getItem('countryCode') === CountryCode.PAN) {
      return String(
        Number(amountsState.partsCostTotal ?? 0) +
          Number(amountsState.ivuPartsAmount ?? 0),
      );
    } else {
      return amountsState.subtotalParts;
    }
  };

  const handleUploadFile = (event: any): void => {
    if (event?.target?.files.length > 0) {
      setFileName(event.target.files[0].name);
      setFileUpload(event.target.files[0]);
    }
  };

  const DepreciationTable: React.FC<DepreciationEstimatesProp> = ({ data }) => {
    if (!data || !data.claimId || data.claimId.length === 0) {
      return null;
    }

    if (
      !data.requiresDepreciationCalculation &&
      !requiresDepreciationByPolicyType
    ) {
      return (
        <div className={'depreciation-table-container'}>
          <h2>Tabla de Depreciación (no aplica)</h2>
          <p style={{ textTransform: 'none' }}>
            En este caso, no se aplica depreciación ya que la persona en la
            póliza no es el asegurado, más la póliza es tipo{' '}
            {CPEClaim.policy?.policyType.toUpperCase() ?? 'SRO o LAP'}. A las
            pólizas de este tipo no se le aplica depreciación.
          </p>
        </div>
      );
    }

    if (!data.requiresDepreciationCalculation) {
      return (
        <div className={'depreciation-table-container'}>
          <h2>Tabla de Depreciación (no aplica)</h2>
          <p style={{ textTransform: 'none' }}>
            En este caso, no se aplica depreciación ya que la persona en la
            póliza no es el asegurado.
          </p>
        </div>
      );
    }

    if (!requiresDepreciationByPolicyType) {
      return (
        <div className={'depreciation-table-container'}>
          <h2>Tabla de Depreciación (no aplica)</h2>
          <p style={{ textTransform: 'none' }}>
            En este caso, la póliza es tipo{' '}
            {CPEClaim.policy?.policyType.toUpperCase() ?? 'SRO o LAP'}. A las
            pólizas de este tipo no se le aplica depreciación.
          </p>
        </div>
      );
    }

    return (
      <div className={'depreciation-table-container'}>
        <h2>Tabla de Depreciación</h2>
        <p style={{ textTransform: 'none' }}>
          Esta tabla contiene los cálculos de Depreciación basado en los
          detalles del vehículo.
        </p>
        <table className={'depreciation-table'}>
          <tbody>
            <tr>
              <td className={'header-row'}>Categoría</td>
              <td className={'header-row'}>Cantidad</td>
              <td className={'header-row'}>Tasa</td>
              <td className={'header-row'}>Total</td>
            </tr>
            <tr>
              <td>Depreciación por año</td>
              <td>{data.carAge} año(s)</td>
              <td>{data.depreciationRatePerYear * 100}% por cada año</td>
              <td className={'total-column'}>
                {data.depreciationByYear.toFixed(2)}%
              </td>
            </tr>
            <tr>
              <td>Depreciación por distancia</td>
              <td>{data.distanceDriven.toLocaleString('en-US')} millas</td>
              <td>
                {data.depreciationRatePerDistanceDriven * 100}% por cada{' '}
                {data.distanceDrivenSegmentLength.toLocaleString('en-US')}{' '}
                millas
              </td>
              <td className={'total-column'}>
                {data.depreciationByDistance.toFixed(2)}%
              </td>
            </tr>
            <tr>
              <td colSpan={3}>Depreciación promedio (entre año y distancia)</td>
              <td className={'total-column'}>
                {data.averageDepreciation.toFixed(2)}%
              </td>
            </tr>
            <tr>
              <td colSpan={3}>Depreciación que se debe aplicar</td>
              <td className={'total-column'}>
                {data.depreciationPercentageToApply.toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div>
        <Grid item xs={12} sm={6} md={6} className="claim-estimated">
          <Grid item xs={12} sm={6} md={6} className="claim-estimated__content">
            <Grid className="claim-estimated__data">
              {InputsEstimated[
                localStorage.getItem('countryCode') as CountryCode
              ].map((column, index) => {
                return (
                  <Grid key={index}>
                    {index === 0 && (
                      <Grid className="claim-estimated__data--title">
                        <p>ESTIMADO</p>
                      </Grid>
                    )}
                    {column.rows.map((row, index2) => {
                      return (
                        <Grid
                          key={`${index}-${index2}`}
                          className={'claim-estimated__' + row.gridClassName}
                        >
                          {row.inputsFields.map((input, index3) => {
                            switch (input.type) {
                              case 'text':
                                return (
                                  <Grid
                                    key={`${index}-${index2}-${index3}`}
                                    className="claim-estimated__data"
                                  >
                                    {input.icon && (
                                      <p className="claim-estimated__data--icon">
                                        {input.icon}
                                      </p>
                                    )}

                                    <TextField
                                      name={input.name}
                                      className={
                                        'claim-estimated__' + input.className
                                      }
                                      label={input.label}
                                      value={amountsState[input.value]}
                                      onChange={onCalcEstimated}
                                      disabled={input.readonly ?? disabled}
                                    ></TextField>
                                  </Grid>
                                );
                              case 'file':
                                return (
                                  <Grid
                                    key={`${index}-${index2}-${index3}`}
                                    style={{ marginLeft: '15px' }}
                                  >
                                    <FileInput
                                      fileName={fileName}
                                      style={{ width: '300px' }}
                                      handleFileChange={handleUploadFile}
                                      disabled={disabled}
                                    />
                                  </Grid>
                                );
                              default:
                                return <></>;
                            }
                          })}
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              })}
            </Grid>
            {dataHistoric?.length > 0 && (
              <Grid className="claim-estimated__historic">
                <Grid>
                  <p>HISTÓRICO DE ESTIMADOS</p>
                </Grid>
                <Grid className="claim-estimated__table">
                  {selectionFields.length > 0 && (
                    <Grid className="claim-estimated__table--options">
                      <span>{selectionFields.length} items selected</span>
                      <Grid className="claim-estimated__table--options--actions">
                        <Button
                          text="Eliminar"
                          variant="text"
                          style={{ color: 'white', fontSize: 14 }}
                          onClick={openConfirmDialog}
                        ></Button>
                        <span>|</span>
                        <Button
                          text="Cancel"
                          variant="text"
                          style={{ color: 'white', fontSize: 14 }}
                          onClick={handleCancel}
                        ></Button>
                      </Grid>
                    </Grid>
                  )}
                  <DataTable
                    columns={columns}
                    data={dataHistoric}
                    showCheckBox={!disabled}
                    selectionModel={selectionFields}
                    onRowSelectionModelChange={(rows: any): void =>
                      setSelectionFields(rows)
                    }
                  ></DataTable>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item xs={12} className="claim-estimated__footer">
            <Button
              disabled={validateAmounts()}
              text="Guardar Estimado"
              onClick={onClickSave}
            />
          </Grid>
        </Grid>
      </div>
      <div>
        <DepreciationTable data={depreciationEstimates} />
        <Grid item xs={12} sm={6} md={6}>
          {renderComponentIsEnable(
            ResourceAction.VIEW_CLAIM_ADJUST_SCREEN,
            accountReference,
          ) && (
            <ClaimAdjust
              claimUId={claimUId}
              depreciationEstimates={depreciationEstimates}
              totalEstimated={amountsState.estimatedTotalAmount}
              requiresDepreciationByPolicyType={
                requiresDepreciationByPolicyType
              }
              subtotalEstimated={getSubtotalCalcCountry()}
              disabled={disabled}
              claimForm={claimForm}
              cpeIds={cpeIds}
              accountReference={accountReference}
              policyNumber={policyNumber}
            />
          )}
        </Grid>
      </div>
      <DialogComponent
        open={openDialog}
        message="El archivo cargado supera el tamaño máximo de 3 MB"
        handleClose={() => setOpenDialog(false)}
      ></DialogComponent>
      <DialogComponent
        messageStyle={{ fontSize: '16px' }}
        message={`¿Desea eliminar los ${selectionFields.length} registros seleccionados?`}
        open={openDialogConfirm}
        handleClose={() => setOpenDialogConfirm(false)}
        handleConfirm={handleConfirm}
      />
    </>
  );
};

export default ClaimEstimated;
